import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FormikHelpers } from 'formik'
import { LoadingIndicator } from '@dhl/components'
import { Card } from '@dhl-ui/components'
import { usePreferenceFormValues } from '~/profile/model/preferences/hooks.js'
import type { PreferenceFormValues } from '~/profile/model/preferences/model.js'
import { useGetProfileLastAddedAddress } from '~/profile/model/profile/hooks.js'
import {
  type AvailableInputModes,
  createValidationSchema,
  PreferredSettingsForm,
} from '~/profile/view/preferences-legacy/components/PreferredSettingsForm.js'
import { useWizardContext, useSetTitle } from '~/setup/model/context.js'
import { StepForm } from '~/setup/view/components/StepForm.js'
import { useSubmitPreferencesForm } from '~/profile/view/preferences-legacy/hooks/useSubmitPreferencesForm.js'

export const DeliveryPreference: FC = () => {
  const { onStepComplete, nextStep } = useWizardContext()
  const { t } = useTranslation()

  useSetTitle(t('portal.profileSetup.deliveryPreference.pageTitle'))
  const { data: lastAddedAddress } = useGetProfileLastAddedAddress({
    query: {
      // use the most up-to-date value of the last added address, since an address was added in the previous step.
      staleTime: 0,
    },
  })
  const addressId = lastAddedAddress?.addressId ?? ''
  const { submitPreferredForm } = useSubmitPreferencesForm()

  const [activeInputMode, setActiveInputMode] = useState<AvailableInputModes | undefined>()
  const {
    data: { initialValues, servicepoints, preference },
    isLoading: isLoadingFormValues,
  } = usePreferenceFormValues(addressId)

  const validationSchema = createValidationSchema(t)

  const clearActiveInputMode = () => setActiveInputMode(undefined)
  const handleServicePointInputOpen = () => setActiveInputMode('servicepoint')
  const handleAgreedPlaceInputOpen = () => setActiveInputMode('agreedplace')
  const handleNotHomeOpen = () => setActiveInputMode('not-home')

  const handleSubmit = async (values: PreferenceFormValues, helpers: FormikHelpers<PreferenceFormValues>) => {
    /**
     * The setup guide does not have a disabled state for the submit button. The code below solves the situation
     * when the user submits the form without having a preference value. In that case, we do not want to save
     * the value as a preference. The user will continue in the setup guide flow.
     */
    if (!values.preference) {
      await onStepComplete()
      return
    }

    submitPreferredForm({
      values,
      initialValues,
      onFailure: () => helpers.setSubmitting(false),
      onSuccess: onStepComplete,
    })
  }

  /** We need a timeout because the fields do not have the actual value. See more about this issue here:
   * https://github.com/jaredpalmer/formik/issues/2083
   */
  const handleOptionConfigured = async () =>
    setTimeout(async () => {
      await nextStep()
      clearActiveInputMode()
    }, 300)

  const isLoadComplete = !isLoadingFormValues && preference !== undefined

  if (!isLoadComplete) {
    return <LoadingIndicator sx={{ height: '100%' }} />
  }

  return (
    <StepForm
      enableReinitialize
      validateOnChange
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {() => (
        <Card>
          <Card.Content>
            <PreferredSettingsForm
              activeInputMode={activeInputMode}
              isAgreedPlacePreferenceAvailable={preference?.agreedPlace?.available || false}
              isServicePointPreferenceAvailable={preference?.servicePoint?.available || false}
              servicepoints={servicepoints}
              onServicePointInputOpen={handleServicePointInputOpen}
              onServicePointInputClose={clearActiveInputMode}
              onAgreedPlaceInputOpen={handleAgreedPlaceInputOpen}
              onAgreedPlaceInputClose={clearActiveInputMode}
              onNotHomeOpen={handleNotHomeOpen}
              onNotHomeClose={clearActiveInputMode}
              onOptionConfigured={handleOptionConfigured}
              saveButtonText={t('portal.profileSetup.wizard.nextButton')}
            />
          </Card.Content>
        </Card>
      )}
    </StepForm>
  )
}
